import SmartImage from "@/components/ui/smart-image";

export default function FeaturesDashboard() {
  return (
    <section id="features" className="py-16 sm:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {/* Buyer Dashboard */}
        <div className="mb-16 sm:mb-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4 sm:mb-6">For Buyers</h2>
              <p className="text-base sm:text-xl text-muted-foreground mb-6 sm:mb-8">
                Find your dream property with AI-powered recommendations, instant valuations, and transparent negotiations.
              </p>
              
              <div className="space-y-6">
                <div className="flex items-start space-x-4" data-testid="feature-propgauge">
                  <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center mt-1">
                    <i className="fas fa-brain text-primary text-sm"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground mb-2">PropGauge™ Valuations</h3>
                    <p className="text-muted-foreground">Get instant, AI-powered property valuations based on market trends and comparable properties.</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4" data-testid="feature-notifications">
                  <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center mt-1">
                    <i className="fas fa-bell text-primary text-sm"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground mb-2">Smart Notifications</h3>
                    <p className="text-muted-foreground">Receive instant alerts when properties matching your criteria become available.</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4" data-testid="feature-analytics">
                  <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center mt-1">
                    <i className="fas fa-chart-line text-primary text-sm"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground mb-2">Market Analytics</h3>
                    <p className="text-muted-foreground">Access detailed market insights and price trends to make informed decisions.</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-muted/20 rounded-2xl p-5 sm:p-8" data-testid="buyer-dashboard-preview">
              <SmartImage
                src="/assets/images/AI_real_estate_workflow_8d6951af.png"
                alt="AI-powered buyer dashboard with property recommendations"
                width={800}
                height={400}
                sizes="(max-width: 1024px) 100vw, 50vw"
                widths={[400, 640, 800, 1200]}
                className="w-full h-64 object-cover rounded-lg mb-6"
              />
              
              <div className="bg-white rounded-lg p-4 apple-shadow">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="font-semibold text-foreground">Property Recommendations</h4>
                  <span className="text-xs bg-primary/10 text-primary px-2 py-1 rounded">AI Powered</span>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center justify-between py-2 border-b border-border">
                    <span className="text-sm text-muted-foreground">3BHK Apartment, Bandra</span>
                    <span className="text-sm font-medium text-foreground">₹2.1 Cr</span>
                  </div>
                  <div className="flex items-center justify-between py-2 border-b border-border">
                    <span className="text-sm text-muted-foreground">Villa, Whitefield</span>
                    <span className="text-sm font-medium text-foreground">₹1.8 Cr</span>
                  </div>
                  <div className="flex items-center justify-between py-2">
                    <span className="text-sm text-muted-foreground">Penthouse, Gurgaon</span>
                    <span className="text-sm font-medium text-foreground">₹3.5 Cr</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Seller Dashboard */}
        <div className="mb-16 sm:mb-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 items-center">
            <div className="order-2 lg:order-1 bg-muted/20 rounded-2xl p-5 sm:p-8" data-testid="seller-dashboard-preview">
              <SmartImage
                src="/assets/images/Modern_apartment_interior_beb39e27.png"
                alt="Modern residential property for seller dashboard"
                width={800}
                height={400}
                sizes="(max-width: 1024px) 100vw, 50vw"
                widths={[400, 640, 800, 1200]}
                className="w-full h-64 object-cover rounded-lg mb-6"
              />
              
              <div className="bg-white rounded-lg p-4 apple-shadow">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="font-semibold text-foreground">Active Listings</h4>
                  <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">3 Active</span>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center justify-between py-2 border-b border-border">
                    <span className="text-sm text-muted-foreground">Koramangala Apartment</span>
                    <span className="text-sm font-medium text-green-600">5 Offers</span>
                  </div>
                  <div className="flex items-center justify-between py-2 border-b border-border">
                    <span className="text-sm text-muted-foreground">Electronic City Villa</span>
                    <span className="text-sm font-medium text-blue-600">2 Offers</span>
                  </div>
                  <div className="flex items-center justify-between py-2">
                    <span className="text-sm text-muted-foreground">HSR Layout House</span>
                    <span className="text-sm font-medium text-gray-500">Pending</span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="order-1 lg:order-2">
              <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4 sm:mb-6">For Sellers</h2>
              <p className="text-base sm:text-xl text-muted-foreground mb-6 sm:mb-8">
                List your property with AI-generated descriptions, manage offers efficiently, and close deals faster.
              </p>
              
              <div className="space-y-6">
                <div className="flex items-start space-x-4" data-testid="feature-smartbid">
                  <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center mt-1">
                    <i className="fas fa-magic text-primary text-sm"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground mb-2">SmartBid™ Management</h3>
                    <p className="text-muted-foreground">AI-powered bid ranking and management system that prioritizes serious buyers.</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4" data-testid="feature-socialai">
                  <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center mt-1">
                    <i className="fas fa-bullhorn text-primary text-sm"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground mb-2">SocialAI™ Marketing</h3>
                    <p className="text-muted-foreground">Automatically promote your listings to targeted audiences across multiple platforms.</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4" data-testid="feature-brokeriq">
                  <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center mt-1">
                    <i className="fas fa-chart-line text-primary text-sm"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground mb-2">BrokerIQ™ Insights</h3>
                    <p className="text-muted-foreground">Get detailed analytics on market performance and optimize your listing strategy.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Service Provider Dashboard */}
        <div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4 sm:mb-6">For Service Providers</h2>
              <p className="text-base sm:text-xl text-muted-foreground mb-6 sm:mb-8">
                Connect with clients instantly, manage jobs efficiently, and grow your reputation with our AI-powered platform.
              </p>
              
              <div className="space-y-6">
                <div className="flex items-start space-x-4" data-testid="feature-servicemate">
                  <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center mt-1">
                    <i className="fas fa-users text-primary text-sm"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground mb-2">ServiceMate™ Matching</h3>
                    <p className="text-muted-foreground">Get matched with clients in real-time based on your skills and location.</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4" data-testid="feature-invoicex">
                  <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center mt-1">
                    <i className="fas fa-receipt text-primary text-sm"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground mb-2">InvoiceX™ Payments</h3>
                    <p className="text-muted-foreground">Automated invoicing and secure payments directly through the platform.</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4" data-testid="feature-propoll">
                  <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center mt-1">
                    <i className="fas fa-star text-primary text-sm"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground mb-2">ProPoll™ Feedback</h3>
                    <p className="text-muted-foreground">Build your reputation with verified client feedback and ratings.</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-muted/20 rounded-2xl p-5 sm:p-8" data-testid="service-provider-dashboard-preview">
              <SmartImage
                src="/assets/images/Service_provider_network_213c45f8.png"
                alt="Service provider network and professional connections"
                width={800}
                height={400}
                sizes="(max-width: 1024px) 100vw, 50vw"
                widths={[400, 640, 800, 1200]}
                className="w-full h-64 object-cover rounded-lg mb-6"
              />
              
              <div className="bg-white rounded-lg p-4 apple-shadow">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="font-semibold text-foreground">Active Jobs</h4>
                  <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">4 Active</span>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center justify-between py-2 border-b border-border">
                    <span className="text-sm text-muted-foreground">Interior Design - Bandra</span>
                    <span className="text-sm font-medium text-green-600">In Progress</span>
                  </div>
                  <div className="flex items-center justify-between py-2 border-b border-border">
                    <span className="text-sm text-muted-foreground">Plumbing - Koramangala</span>
                    <span className="text-sm font-medium text-blue-600">Scheduled</span>
                  </div>
                  <div className="flex items-center justify-between py-2">
                    <span className="text-sm text-muted-foreground">Painting - Electronic City</span>
                    <span className="text-sm font-medium text-orange-600">Quoted</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
